import { TranslateService } from '@ngx-translate/core';

import { registerLanguages } from 'src/config/languages';
import { UserService } from '@dwo/services';
import { ChangeSyncService } from './core/services/change-sync.service';
import { ChangeTraceService } from './core/services/change-trace.service';

import { filter, first } from 'rxjs/operators';
import { NetworkStatusService } from '@dwo/services';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Component, OnInit } from '@angular/core';

import { AppService, ApplicationMode } from './core/services/app.service';
import { FeatureFlagService } from './core/services/feature-flag.service';
import { AuthSessionService } from './core/services/auth-session.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CurrentUserLoad } from '@dwo/actions';
import { ConfirmComponent } from '@app/shared/confirm/confirm.component';
import { ConfirmDialogModel } from '@app/shared/confirm/confirm-dialog.model';
import { loginRequest } from '@app/auth-config';
import { ReleaseNoteService } from './core/services/release-note.service';
import { isE2eBypassEnabled } from './auth/e2e-bypass';
import { isReachableProbeResponse } from '@app/core/utils/probe-reachability.util';
import { FeatureFlagKey } from '@dwo/enums';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false,
})
export class LayoutComponent implements OnInit {
  title = 'Digital Work Order';
  language: string;
  loading = true;
  offlineMode = true;
  online = true;
  homeUrl = '/';
  // Gates the old toolbar/dialog offline UI, being phased out in favor of the newer offline flow.
  legacyOfflineUiEnabled = false;

  private readonly authCompelete$ = new BehaviorSubject<boolean>(false);
  private authComplete: boolean = false;
  private loginAttempted = false;
  private readonly destroy$ = new Subject<boolean>();

  constructor(
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly authService: MsalService,
    private translateService: TranslateService,
    private changeSyncService: ChangeSyncService,
    private changeTraceService: ChangeTraceService,
    private userService: UserService,
    private appService: AppService,
    private featureFlagService: FeatureFlagService,
    private router: Router,
    private dialog: MatDialog,
    private networkStatusService: NetworkStatusService,
    private readonly store: Store,
    private readonly authSessionService: AuthSessionService,
    private readonly releaseNoteService: ReleaseNoteService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.router.url.startsWith('/callback')) {
      return;
    }

    registerLanguages(this.translateService);
    this.setLanguage(localStorage.getItem('language') || 'en');

    await this.changeTraceService.register();
    this.changeSyncService.register();

    if (isE2eBypassEnabled()) {
      // Skip the Azure AD redirect flow: the e2e backend accepts a synthetic
      // principal via headers, so we mark auth complete and start the session.
      this.authComplete = true;
      this.authSessionService.startSession();
      this.authCompelete$.next(true);
    } else {
      // Wait for MSAL to finish redirect handling
      this.msalBroadcastService.inProgress$
        .pipe(filter((status) => status === InteractionStatus.None))
        .subscribe(async () => {
          const accounts = this.authService.instance.getAllAccounts();

          if (!accounts.length && !this.loginAttempted) {
            this.loginAttempted = true;
            await this.authService.instance.loginRedirect(loginRequest);
            return;
          }

          this.authComplete = true;
          this.authSessionService.startSession();
          this.authCompelete$.next(true);
        });
    }

    this.authCompelete$.subscribe(async (complete) => {
      if (complete) {
        this.store.dispatch(new CurrentUserLoad());
        await this.userService.load();
        await this.appService.load();
        const user = this.userService.get();
        await this.featureFlagService.load(user?.email || user?.username);
        this.legacyOfflineUiEnabled = this.featureFlagService.isEnabled(FeatureFlagKey.ShowLegacyOfflineUi);
      }
    });

    this.appService.getMode().subscribe((mode) => {
      this.offlineMode = mode === ApplicationMode.OFFLINE;
      this.homeUrl = this.offlineMode ? '/offline' : '/';
    });

    this.networkStatusService.isOnline().subscribe((online) => {
      if (online) {
        this.hideOfflineMessageDialog();
      } else {
        this.showOfflineMessageDialog();
      }
      this.online = online;
    });

    this.loading = false;
  }

  setLanguage(language: string) {
    localStorage.setItem('language', language);
    this.language = language;
    this.translateService.use(language);
  }

  hideOfflineMessageDialog(): void {
    this.dialog.closeAll();
  }

  reloadWorkOrders(): void {
    this.router.navigateByUrl('/');
  }

  showOfflineMessageDialog(): void {
    if (!this.legacyOfflineUiEnabled || this.offlineMode) {
      return;
    }

    const message = this.translateService.instant('app.offlineWarning.message');
    const confirmMessage = this.translateService.instant('app.offlineWarning.description');

    const dialog = this.dialog.open(ConfirmComponent, {
      data: {
        message,
        confirmMessage,
        hideCancelButton: true,
        icon: 'warning',
      } as ConfirmDialogModel,
    });

    dialog
      .afterClosed()
      .pipe(first())
      .subscribe(async (result) => {
        if (!result) {
          return;
        }

        // The browser may report offline while the backend is still reachable.
        // Verify against the check-update endpoint and only go offline if it fails.
        if (await this.isBackendReachable()) {
          return;
        }

        // redirect to offline mode
        this.appService.setMode(ApplicationMode.OFFLINE);
        this.router.navigateByUrl('/offline');
      });
  }

  private async isBackendReachable(): Promise<boolean> {
    try {
      await firstValueFrom(this.releaseNoteService.checkVersionUpdate());
      return true;
    } catch (error: unknown) {
      if (isReachableProbeResponse(error)) {
        return true;
      }
      return false;
    }
  }
}
